// src/features/gigServices/gigServicesSlice.js
// The seller's OWN Services catalog — same job and shape as
// reviewServicesSlice.js (a creator's own listings), which this mirrors.
//
// Orders, chat and the seller's tax profile deliberately get NO slice: they're
// per-page, refetched on every visit, and shared across no two routes. Adding
// them here would mean a stale order sitting in the store behind a "Delivered"
// badge the buyer already acted on — the existing Events and Review My Work
// features made the same call.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

const initialState = {
  gigs: [],
  currentGig: null,
  loading: false,
  error: null,
};

export const fetchGigs = createAsyncThunk(
  'gigServices/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await apiService.getMyGigs();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load your services');
    }
  }
);

export const fetchGigById = createAsyncThunk(
  'gigServices/fetchById',
  async (gigId, { rejectWithValue }) => {
    try {
      return await apiService.getGig(gigId);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load this service');
    }
  }
);

export const createGigThunk = createAsyncThunk(
  'gigServices/create',
  async (data, { rejectWithValue }) => {
    try {
      return await apiService.createGig(data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to publish service');
    }
  }
);

export const updateGigThunk = createAsyncThunk(
  'gigServices/update',
  async ({ gigId, updates }, { rejectWithValue }) => {
    try {
      return await apiService.updateGig(gigId, updates);
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to save changes');
    }
  }
);

export const deleteGigThunk = createAsyncThunk(
  'gigServices/delete',
  async (gigId, { rejectWithValue }) => {
    try {
      await apiService.deleteGig(gigId);
      return gigId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete service');
    }
  }
);

export const setGigPausedThunk = createAsyncThunk(
  'gigServices/setPaused',
  async ({ gigId, paused }, { rejectWithValue }) => {
    try {
      return await apiService.setGigPaused(gigId, paused);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update availability');
    }
  }
);

// Both update paths return the saved listing, and both have to patch the same
// two places (the list row and currentGig if it's the one open).
const applyUpdate = (state, gig) => {
  if (!gig?.gig_id) return;
  const index = state.gigs.findIndex((g) => g.gig_id === gig.gig_id);
  if (index !== -1) state.gigs[index] = { ...state.gigs[index], ...gig };
  if (state.currentGig?.gig_id === gig.gig_id) {
    state.currentGig = { ...state.currentGig, ...gig };
  }
};

const gigServicesSlice = createSlice({
  name: 'gigServices',
  initialState,
  reducers: {
    setCurrentGig: (state, action) => {
      state.currentGig = action.payload;
    },
    clearGigsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGigs.fulfilled, (state, action) => {
        state.loading = false;
        state.gigs = action.payload;
      })
      .addCase(fetchGigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchGigById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGigById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGig = action.payload;
      })
      .addCase(fetchGigById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createGigThunk.fulfilled, (state, action) => {
        state.gigs.unshift(action.payload);
        state.currentGig = action.payload;
      })
      .addCase(createGigThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateGigThunk.fulfilled, (state, action) => {
        applyUpdate(state, action.payload);
      })
      .addCase(updateGigThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(setGigPausedThunk.fulfilled, (state, action) => {
        applyUpdate(state, action.payload);
      })
      .addCase(setGigPausedThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteGigThunk.fulfilled, (state, action) => {
        state.gigs = state.gigs.filter((g) => g.gig_id !== action.payload);
        if (state.currentGig?.gig_id === action.payload) state.currentGig = null;
      })
      .addCase(deleteGigThunk.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setCurrentGig, clearGigsError } = gigServicesSlice.actions;
export default gigServicesSlice.reducer;
